import { InformationCircleIcon } from '@heroicons/react/24/outline';
import SolicitacoesKanbanCard from './SolicitacoesKanbanCard';
import type { ColunaComputada, ItemComputado } from './solicitacoesKanban.utils';

interface SolicitacoesKanbanColunaProps {
  coluna: ColunaComputada;
  itensDaColuna: ItemComputado[];
  /** Substitui o "Nada aqui." padrão — usado quando a coluna só está vazia porque o filtro
   * de Status da tela está escondendo esse status, não porque não existe nenhuma. */
  mensagemVazia?: string;
  larguraPx: number;
  onIniciarArrasteLargura: (event: React.MouseEvent) => void;
  arrastavel: boolean;
  /** Só preenchido quando `!arrastavel` por essa coluna não ser de status no modo
   * Personalizado — explica o motivo em vez de deixar parecer que travou. */
  avisoSemDrag?: string;
  sobreArraste: boolean;
  dropRejeitado: boolean;
  onDragEnter: () => void;
  onDragLeave: () => void;
  /** Recebe só o id (arrastado de fora desta coluna, na maioria dos casos) — quem resolve
   * pro objeto Solicitacao completo é o pai, que tem a lista inteira, não só esta coluna. */
  onDrop: (id: string) => void;
  onSelect: (id: string) => void;
}

export default function SolicitacoesKanbanColuna({
  coluna,
  itensDaColuna,
  mensagemVazia,
  larguraPx,
  onIniciarArrasteLargura,
  arrastavel,
  avisoSemDrag,
  sobreArraste,
  dropRejeitado,
  onDragEnter,
  onDragLeave,
  onDrop,
  onSelect,
}: SolicitacoesKanbanColunaProps) {
  // Anel sozinho (ring-2 ring-red-400) ficava fraco demais contra o fundo cinza claro da
  // coluna — junto com um tingimento de fundo fica bem mais perceptível sem precisar de um
  // anel grosso (que tinha ficado forte demais). O fundo base (bg-gray-50/bg-red-50) precisa
  // ser condicional aqui, e não uma classe fixa lá embaixo — duas classes de background na
  // mesma string não garantem qual "ganha" no CSS gerado.
  const classeFundo = dropRejeitado ? 'bg-red-50' : 'bg-gray-50';
  const anelDestaque = sobreArraste
    ? 'ring-2 ring-blue-400'
    : dropRejeitado
      ? 'ring-2 ring-red-500 transition-colors duration-200'
      : '';

  return (
    <div
      onDragOver={(event) => arrastavel && event.preventDefault()}
      onDragEnter={() => arrastavel && onDragEnter()}
      onDragLeave={() => arrastavel && onDragLeave()}
      onDrop={(event) => {
        if (!arrastavel) return;
        event.preventDefault();
        onDrop(event.dataTransfer.getData('text/plain'));
      }}
      className={`relative flex flex-shrink-0 flex-col rounded-md ${classeFundo} ${anelDestaque}`}
      style={{ width: `${larguraPx}px` }}
    >
      <div className="sticky top-0 flex items-center gap-1 rounded-t-md bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {coluna.label} ({itensDaColuna.length})
        {avisoSemDrag && (
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-amber-500" title={avisoSemDrag} />
        )}
      </div>
      {/* Grade com largura de coluna ajustável (ver divisória arrastável abaixo) — quantos
          cards cabem por linha é automático (CSS `auto-fill`), não um número fixo: arrastar
          a coluna mais larga já cabe mais um card por linha sozinho. Altura usa o espaço
          real da tela (menos cabeçalho/abas/filtros acima), não um vh fixo pequeno. */}
      <div
        className="grid content-start gap-2 overflow-y-auto p-2"
        style={{ maxHeight: 'calc(100vh - 280px)', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
      >
        {itensDaColuna.length === 0 && (
          <p
            className={`p-2 text-xs ${mensagemVazia ? 'text-amber-600' : 'text-gray-400'}`}
            style={{ gridColumn: '1 / -1' }}
          >
            {mensagemVazia ?? 'Nada aqui.'}
          </p>
        )}
        {itensDaColuna.map(({ solicitacao, alerta, pendencia, dataFormatada, clienteNome }) => (
          <SolicitacoesKanbanCard
            key={solicitacao.id}
            solicitacao={solicitacao}
            alerta={alerta}
            pendencia={pendencia}
            dataFormatada={dataFormatada}
            clienteNome={clienteNome}
            arrastavel={arrastavel}
            onSelect={onSelect}
          />
        ))}
      </div>

      {/* Divisória arrastável — mesmo padrão do modo "Dividida" (SolicitacoesSplitView):
          barra fina na borda direita, arraste ajusta a largura em pixels (persistida por
          coluna). Absoluta (não um flex-sibling) pra não precisar reestruturar o layout de
          `flex gap-4` do board inteiro. */}
      <div
        onMouseDown={onIniciarArrasteLargura}
        className="group absolute right-0 top-0 flex h-full w-2 cursor-col-resize items-center justify-center rounded-r-md"
        title="Arraste pra ajustar a largura da coluna"
      >
        <div className="h-8 w-0.5 rounded-full bg-gray-300 group-hover:bg-blue-400" />
      </div>
    </div>
  );
}
